import { useEffect, useRef, useState } from 'react';
import {
  IconCalendar,
  IconFilter,
  IconPackage,
  IconPlus,
  IconSearch,
  IconSend,
  IconShip,
  IconSparkles,
  IconTrash,
} from '@tabler/icons-react';
import { Button, Chip, Drawer, Input, Modal, Segmented, Select, SelectionBar, StateView, Table, useTheme, useToast, type Column } from '@hana/ui';
import s from './Kit.module.css';

// Trang mẫu thành phần (board Hybrid-Kit): để chụp Playwright và để luồng B tra cách dùng. Không gọi dữ liệu thật.
// ?open=modal|drawer|confirm|toast mở sẵn lớp nổi để chụp ảnh.

interface Row {
  id: string;
  cus: string;
  route: string;
  etd: string;
  st: 'Active' | 'Standby' | 'Released';
  kind: 'FCL' | 'LCL' | 'Air';
}
const ROWS: Row[] = [
  { id: 'SE260622020', cus: 'GREENBUD', route: 'VNSGN → KRPUS', etd: '24/Oct/2026', st: 'Active', kind: 'FCL' },
  { id: 'SE260631180', cus: 'Dệt may An Phú', route: 'VNCLI → USLAX', etd: '26/Oct/2026', st: 'Standby', kind: 'FCL' },
  { id: 'SE260629040', cus: 'THANHHAO', route: 'VNSGN → JPTYO', etd: '27/Oct/2026', st: 'Released', kind: 'LCL' },
  { id: 'AE260630011', cus: 'TAMNGOC', route: 'SGN → ICN', etd: '28/Oct/2026', st: 'Active', kind: 'Air' },
  { id: 'SE260637980', cus: 'Gỗ Minh Long', route: 'VNHPH → CNSHA', etd: '29/Oct/2026', st: 'Standby', kind: 'FCL' },
];
const DOT: Record<Row['st'], string> = { Active: 'var(--d-blue)', Standby: 'var(--d-amber)', Released: 'var(--d-green)' };

const COLS: Column<Row>[] = [
  { key: 'id', header: 'Số job', width: 150, cell: (r) => <b className={s.id}>{r.id}</b> },
  { key: 'cus', header: 'Khách hàng', cell: (r) => r.cus },
  { key: 'route', header: 'Tuyến', width: 170, minor: true, cell: (r) => r.route },
  {
    key: 'kind',
    header: 'Loại',
    width: 100,
    minor: true,
    cell: (r) => (
      <Chip label="Loại hàng" icon={r.kind === 'Air' ? <IconSend size={13} /> : <IconShip size={13} />}>
        {r.kind}
      </Chip>
    ),
  },
  { key: 'etd', header: 'ETD', width: 130, minor: true, cell: (r) => r.etd },
  {
    key: 'st',
    header: 'Trạng thái',
    width: 130,
    cell: (r) => (
      <Chip label="Trạng thái" dot={DOT[r.st]}>
        {r.st}
      </Chip>
    ),
  },
];

type Tab = 'all' | 'Active' | 'Standby';

export function Kit() {
  const { theme, toggle } = useTheme();
  const toast = useToast();
  const open0 = new URLSearchParams(location.search).get('open');
  const [modal, setModal] = useState(open0 === 'modal');
  const [confirm, setConfirm] = useState(open0 === 'confirm');
  const [drawer, setDrawer] = useState(open0 === 'drawer');
  const [sel, setSel] = useState<Set<string>>(new Set(['SE260631180', 'SE260637980']));
  const [tab, setTab] = useState<Tab>('all');
  const [dens, setDens] = useState<'std' | 'dense'>('std');

  const shown = useRef(false);
  useEffect(() => {
    if (open0 === 'toast' && !shown.current) {
      shown.current = true;
      toast({ msg: 'Đã đổi → Released', undo: () => {} });
      toast({ msg: 'Không lưu được, thử lại sau', tone: 'err' });
    }
  }, [open0, toast]);

  const rows = tab === 'all' ? ROWS : ROWS.filter((r) => r.st === tab);

  return (
    <div className={s.page}>
      <header className={s.head}>
        <h1>UI kit</h1>
        <Button variant="soft" onClick={toggle}>
          {theme === 'dark' ? 'Giao diện sáng' : 'Giao diện tối'}
        </Button>
      </header>

      <section className={s.card} aria-label="Nút và chip">
        <h2>Nút · chip · tab</h2>
        <div className={s.row}>
          <Button variant="primary" icon={<IconPlus size={16} />}>
            Tạo job
          </Button>
          <Button variant="secondary">Phụ</Button>
          <Button variant="soft" icon={<IconFilter size={16} />}>
            Lọc
          </Button>
          <Button variant="ghost">Bỏ qua</Button>
          <Button variant="ask" icon={<IconSparkles size={16} />}>
            Ask HANA
          </Button>
          <Button variant="danger" icon={<IconTrash size={16} />}>
            Huỷ job
          </Button>
          <Button variant="primary" busy>
            Đang lưu
          </Button>
          <Button variant="secondary" aria-label="Tìm" icon={<IconSearch size={18} />} />
          <Button variant="primary" disabled title="Chỉ Kế toán xuất được hoá đơn">
            Xuất hoá đơn
          </Button>
        </div>
        <div className={s.row}>
          <Chip label="Trạng thái" dot="var(--d-blue)">
            Active
          </Chip>
          <Chip label="Trạng thái" dot="var(--d-amber)">
            Standby
          </Chip>
          <Chip label="Trạng thái" dot="var(--d-green)">
            Released
          </Chip>
          <Chip label="Loại hàng" icon={<IconPackage size={13} />}>
            LCL
          </Chip>
          <Chip label="ETD" icon={<IconCalendar size={13} />}>
            24/Oct
          </Chip>
          <Chip tone="ok">+6,1%</Chip>
          <Chip tone="warn">−0,8 đ</Chip>
          <Chip tone="err">quá hạn</Chip>
          <Chip tone="info" label="Dịch vụ" onRemove={() => {}}>
            Sea FCL, Air
          </Chip>
        </div>
        <div className={s.row}>
          <Segmented<Tab>
            label="Trạng thái job"
            value={tab}
            onChange={setTab}
            items={[
              { key: 'all', label: 'Tất cả', n: ROWS.length },
              { key: 'Active', label: 'Active', n: 2, dot: 'var(--d-blue)' },
              { key: 'Standby', label: 'Standby', n: 2, dot: 'var(--d-amber)' },
            ]}
          />
          <Segmented
            kind="toggle"
            label="Mật độ"
            value={dens}
            onChange={setDens}
            items={[
              { key: 'std', label: 'Chuẩn' },
              { key: 'dense', label: 'Gọn' },
            ]}
          />
        </div>
      </section>

      <section className={s.card} aria-label="Ô nhập">
        <h2>Ô nhập · select</h2>
        <div className={s.grid}>
          <Input label="Khách hàng" required icon={<IconSearch size={15} />} placeholder="Tìm khách hàng…" />
          <Input label="ETD · ngày giờ" defaultValue="24/Oct/2026 11:00" suffix={<IconCalendar size={16} color="var(--lb)" />} hint="Lịch hãng 00:00 · AIS +11h" />
          <Select label="Loại hàng" options={['FCL', 'LCL', 'Air', 'Truck']} defaultValue="FCL" />
          <Input label="Khách hàng (lỗi)" error="Chọn một khách hàng trong danh sách để tạo job." placeholder="Chọn khách hàng…" />
          <Input label="Giá mua (cost)" locked hint="Chỉ Sales Manager và Kế toán xem được giá mua." />
        </div>
      </section>

      <section className={s.card} aria-label="Lớp nổi">
        <h2>Lớp nổi</h2>
        <div className={s.row}>
          <Button variant="secondary" onClick={() => setModal(true)}>
            Modal
          </Button>
          <Button variant="secondary" onClick={() => setConfirm(true)}>
            Xác nhận
          </Button>
          <Button variant="secondary" onClick={() => setDrawer(true)}>
            Ngăn kéo
          </Button>
          <Button variant="secondary" onClick={() => toast({ msg: 'Đã đổi → Released', undo: () => toast({ msg: 'Đã hoàn tác' }) })}>
            Toast · Hoàn tác
          </Button>
          <Button variant="secondary" onClick={() => toast({ msg: 'Không lưu được, thử lại sau', tone: 'err' })}>
            Toast lỗi
          </Button>
        </div>
      </section>

      <section className={s.card} aria-label="Bảng">
        <h2>Bảng · tiêu đề dính · chọn dòng</h2>
        <SelectionBar count={sel.size} noun="job" onClear={() => setSel(new Set())}>
          <Button size="sm" variant="secondary">
            Nhắc gửi chứng từ
          </Button>
        </SelectionBar>
        <Table label="Danh sách job" columns={COLS} rows={rows} rowKey={(r) => r.id} selected={sel} onSelect={setSel} dense={dens === 'dense'} maxHeight={260} />
      </section>

      <div className={s.states}>
        {(['loading', 'empty', 'error', 'noperm'] as const).map((st) => (
          <section key={st} className={s.card} aria-label={st}>
            <StateView
              state={st}
              title={st === 'empty' ? 'Chưa có job Standby' : st === 'error' ? 'Không tải được danh sách job' : undefined}
              desc={
                st === 'empty'
                  ? 'Không có job nào đang chờ chứng từ trong kỳ 10/2026.'
                  : st === 'error'
                    ? 'Mất kết nối tới máy chủ. Dữ liệu đang hiện là bản lúc 22:12.'
                    : st === 'noperm'
                      ? 'Chỉ Sales Manager và Kế toán xem được.'
                      : undefined
              }
              action={
                st === 'empty' ? (
                  <Button variant="primary" icon={<IconPlus size={16} />}>
                    Tạo job
                  </Button>
                ) : st === 'error' ? (
                  <Button variant="secondary">Thử lại</Button>
                ) : undefined
              }
            />
          </section>
        ))}
      </div>

      <Modal
        open={modal}
        onOpenChange={setModal}
        title="Nhắc khách gửi chứng từ"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModal(false)}>
              Huỷ
            </Button>
            <Button variant="primary" icon={<IconSend size={16} />} onClick={() => setModal(false)}>
              Gửi nhắc nhở
            </Button>
          </>
        }
      >
        <Select label="Kênh" options={['Email', 'Zalo OA']} />
        <Input label="Người nhận" defaultValue="docs@anphu.vn" />
      </Modal>

      <Modal
        danger
        open={confirm}
        onOpenChange={setConfirm}
        title="Huỷ job SE260637980?"
        description="Không hoàn tác được nếu đã xuất hoá đơn."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(false)}>
              Giữ job
            </Button>
            <Button variant="danger" onClick={() => setConfirm(false)}>
              Huỷ job
            </Button>
          </>
        }
      >
        <Input label="Lý do" placeholder="Khách huỷ booking…" />
      </Modal>

      <Drawer
        open={drawer}
        onOpenChange={setDrawer}
        title="Tạo job mới"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDrawer(false)}>
              Huỷ
            </Button>
            <Button variant="primary" onClick={() => setDrawer(false)}>
              Tạo job
            </Button>
          </>
        }
      >
        <Input label="Khách hàng" required icon={<IconSearch size={15} />} placeholder="Tìm khách hàng…" />
        <div className={s.grid2}>
          <Select label="Dịch vụ" options={['Sea FCL', 'Sea LCL', 'Air']} />
          <Select label="Điều kiện" options={['FOB', 'CIF', 'EXW']} />
          <Input label="POL" defaultValue="VNCLI" />
          <Input label="POD" defaultValue="USLAX" />
          <Input label="ETD" defaultValue="24/Oct/2026" />
          <Input label="Số cont" defaultValue="2 × 40HC" />
        </div>
      </Drawer>
    </div>
  );
}
